import React, { useEffect, useState } from 'react';
import { App as AntdApp, Card, Row, Col, Button, Space, Tag, Badge, Form, Input, Empty, Spin, Popconfirm, Checkbox, theme } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, HolderOutlined, SearchOutlined } from '@ant-design/icons';
import * as Icons from '@ant-design/icons';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { trpc } from '../../lib/trpc';
import { IconPicker } from '../../components/IconPicker';
import { withDevModalFilePath, HexColorPicker } from '../../components/common';

const { Modal } = withDevModalFilePath('client/src/pages/settings/EquipmentCategoriesTab.tsx');

interface EquipmentCategory {
  id: string;
  companyId: string;
  categoryLabel: string;
  description?: string | null;
  displayOrder: number;
  color?: string | null;
  icon?: string | null;
  isActive: boolean;
  usageCount?: number;
  createdAt: string | Date;
  updatedAt: string | Date;
}

const sanitizeDescriptionValue = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;
const sanitizeIconValue = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

// ソート可能なアイテムコンポーネント
const SortableItem: React.FC<{
  category: EquipmentCategory;
  isSelected: boolean;
  onClick: () => void;
  getIconComponent: (iconName?: string | null) => React.ReactNode;
  token: ReturnType<typeof theme.useToken>['token'];
}> = ({ category, isSelected, onClick, getIconComponent, token }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: isDragging ? 'grabbing' : 'pointer',
    backgroundColor: isSelected ? token.colorPrimaryBg : 'transparent',
    padding: '12px',
    borderRadius: '4px',
    opacity: isDragging ? 0.5 : 1,
  };

  const resolvedColor = category.color ?? '#ffffff';

  return (
    <div ref={setNodeRef} style={style} onClick={onClick}>
      <Space>
        <div {...attributes} {...listeners} style={{ cursor: 'grab', display: 'flex', alignItems: 'center' }}>
          <HolderOutlined style={{ color: token.colorTextTertiary, fontSize: 16 }} />
        </div>
        {category.icon && getIconComponent(category.icon)}
        <div
          style={{
            width: 16,
            height: 16,
            backgroundColor: resolvedColor,
            borderRadius: '2px',
            border: `1px solid ${token.colorBorder}`,
          }}
        />
        <strong>{category.categoryLabel}</strong>
        <Tag color={category.isActive ? 'green' : 'default'}>
          {category.isActive ? '有効' : '無効'}
        </Tag>
        {category.usageCount !== undefined && category.usageCount > 0 && (
          <Badge count={category.usageCount} showZero color="green" />
        )}
      </Space>
    </div>
  );
};

const EquipmentCategoriesTab: React.FC = () => {
  const { token } = theme.useToken();
  const { message: messageApi } = AntdApp.useApp();
  const [searchText, setSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<EquipmentCategory | null>(null);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [form] = Form.useForm();
  const utils = trpc.useUtils();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // categoryKeyは削除されたため、自動生成ロジックも不要

  // カテゴリ一覧取得（使用数付き）
  const { data: categories = [], isLoading } = trpc.equipmentCategory.listWithUsage.useQuery({
    includeInactive: true,
    sortBy: 'display_order',
    sortOrder: 'asc',
  });

  const [orderedCategories, setOrderedCategories] = useState<EquipmentCategory[]>([]);

  useEffect(() => {
    const normalizedCategories = categories as unknown as EquipmentCategory[];
    setOrderedCategories(normalizedCategories);
    if (selectedCategory) {
      const refreshed =
        normalizedCategories.find((c) => c.id === selectedCategory.id) ?? null;
      setSelectedCategory(refreshed);
    }
  }, [categories, selectedCategory]);

  // 並べ替えミューテーション
  const reorderMutation = trpc.equipmentCategory.reorder.useMutation();

  // カテゴリ作成
  const createMutation = trpc.equipmentCategory.create.useMutation({
    onSuccess: async () => {
      messageApi.success('設備カテゴリを作成しました');
      await utils.equipmentCategory.listWithUsage.invalidate();
      setIsCreateModalVisible(false);
      form.resetFields();
    },
    onError: (error) => {
      const errorCode = (error as { data?: { code?: string } } | undefined)?.data?.code;
      if (errorCode === 'CONFLICT') {
        form.setFields([{ name: 'categoryLabel', errors: [error.message] }]);
      }
      messageApi.error(error.message || 'カテゴリの作成に失敗しました');
    },
  });

  // カテゴリ更新
  const updateMutation = trpc.equipmentCategory.update.useMutation({
    onSuccess: async () => {
      messageApi.success('設備カテゴリを更新しました');
      await utils.equipmentCategory.listWithUsage.invalidate();
      setIsEditMode(false);
    },
    onError: (error) => {
      const errorCode = (error as { data?: { code?: string } } | undefined)?.data?.code;
      if (errorCode === 'CONFLICT') {
        form.setFields([{ name: 'categoryLabel', errors: [error.message] }]);
      }
      messageApi.error(error.message || 'カテゴリの更新に失敗しました');
    },
  });

  // カテゴリ削除
  const deleteMutation = trpc.equipmentCategory.delete.useMutation({
    onSuccess: async () => {
      messageApi.success('設備カテゴリを削除しました');
      await utils.equipmentCategory.listWithUsage.invalidate();
      setSelectedCategory(null);
    },
    onError: (error) => {
      messageApi.error(error.message || 'カテゴリの削除に失敗しました');
    },
  });

  // ドラッグ終了時の処理
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = orderedCategories.findIndex((cat) => cat.id === active.id);
    const newIndex = orderedCategories.findIndex((cat) => cat.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const previousItems = orderedCategories;
    const newOrder = arrayMove(orderedCategories, oldIndex, newIndex);
    setOrderedCategories(newOrder);

    // 新しい表示順序を計算してAPIに送信
    const reorderItems = newOrder.map((cat, index) => ({
      id: cat.id,
      displayOrder: index,
    }));

    reorderMutation.mutate(
      { items: reorderItems },
      {
        onSuccess: async () => {
          messageApi.success('表示順序を更新しました');
          await utils.equipmentCategory.listWithUsage.invalidate();
        },
        onError: (error) => {
          setOrderedCategories(previousItems);
          messageApi.error(error.message || '表示順序の更新に失敗しました');
        },
      }
    );
  };

  // カテゴリ選択
  const handleSelectCategory = (category: EquipmentCategory) => {
    setSelectedCategory(category);
    setIsEditMode(false);
    form.setFieldsValue({
      categoryLabel: category.categoryLabel,
      description: category.description ?? '',
      color: category.color || '#ffffff',
      icon: category.icon,
      isActive: category.isActive,
    });
  };

  // 新規作成モーダルを開く
  const handleOpenCreateModal = () => {
    form.resetFields();
    form.setFieldsValue({
      color: '#ffffff',
      displayOrder: orderedCategories.length,
      isActive: true,
    });
    setIsCreateModalVisible(true);
  };

  // カテゴリ作成
  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      const resolvedColor = typeof values.color === 'string' ? values.color : values.color?.toHexString();
      const colorValue = resolvedColor ?? '#ffffff';
      createMutation.mutate({
        categoryLabel: values.categoryLabel,
        description: sanitizeDescriptionValue(values.description),
        displayOrder: orderedCategories.length,
        color: colorValue,
        icon: sanitizeIconValue(values.icon),
      });
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  // カテゴリ更新
  const handleUpdate = async () => {
    if (!selectedCategory) return;
    try {
      const values = await form.validateFields();
      const resolvedColor = typeof values.color === 'string' ? values.color : values.color?.toHexString();
      const colorValue = resolvedColor ?? '#ffffff';
      updateMutation.mutate({
        id: selectedCategory.id,
        categoryLabel: values.categoryLabel,
        description: sanitizeDescriptionValue(values.description),
        color: colorValue,
        icon: sanitizeIconValue(values.icon),
        isActive: values.isActive,
      });
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  // カテゴリ削除
  const handleDelete = (categoryId: string) => {
    deleteMutation.mutate({ id: categoryId });
  };

  // フィルタリング
  const filteredCategories = orderedCategories.filter(
    (cat) =>
      !searchText ||
      cat.categoryLabel.toLowerCase().includes(searchText.toLowerCase())
  );

  // アイコンコンポーネントを取得
  const getIconComponent = (iconName?: string | null) => {
    if (!iconName) return null;
    const iconKey = iconName as keyof typeof Icons;
    const IconComponent = Icons[iconKey] as React.ComponentType<{ style?: React.CSSProperties }> | undefined;
    return IconComponent ? <IconComponent style={{ fontSize: 16 }} /> : null;
  };

  return (
    <Row gutter={16}>
      {/* 左パネル: カテゴリ一覧 */}
      <Col xs={24} lg={10}>
        <Card
          title="設備カテゴリ一覧"
          extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreateModal}>
              新規作成
            </Button>
          }
        >
          <Input
            placeholder="カテゴリを検索"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ marginBottom: 16 }}
            allowClear
          />

          <Spin spinning={isLoading}>
            {filteredCategories.length === 0 ? (
              <Empty description="カテゴリがありません" />
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={filteredCategories.map((cat) => cat.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {filteredCategories.map((category) => (
                      <SortableItem
                        key={category.id}
                        category={category}
                        isSelected={selectedCategory?.id === category.id}
                        onClick={() => handleSelectCategory(category)}
                        getIconComponent={getIconComponent}
                        token={token}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </Spin>
        </Card>
      </Col>

      {/* 右パネル: カテゴリ詳細/編集 */}
      <Col xs={24} lg={14}>
        <Card
          title={selectedCategory ? `カテゴリ詳細: ${selectedCategory.categoryLabel}` : 'カテゴリを選択してください'}
          extra={
            selectedCategory && (
              <Space>
                {!isEditMode ? (
                  <Button icon={<EditOutlined />} onClick={() => setIsEditMode(true)}>
                    編集
                  </Button>
                ) : (
                  <>
                    <Button onClick={() => setIsEditMode(false)}>キャンセル</Button>
                    <Button type="primary" onClick={handleUpdate} loading={updateMutation.isPending}>
                      保存
                    </Button>
                  </>
                )}
                <Popconfirm
                  title="本当に削除しますか？"
                  description={
                    selectedCategory.usageCount && selectedCategory.usageCount > 0
                      ? `${selectedCategory.usageCount}件の設備がこのカテゴリを使用しています。`
                      : undefined
                  }
                  onConfirm={() => handleDelete(selectedCategory.id)}
                  okText="削除"
                  cancelText="キャンセル"
                  disabled={!!selectedCategory.usageCount && selectedCategory.usageCount > 0}
                >
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    disabled={!!selectedCategory.usageCount && selectedCategory.usageCount > 0}
                  >
                    削除
                  </Button>
                </Popconfirm>
              </Space>
            )
          }
        >
          {!selectedCategory ? (
            <Empty description="左のリストからカテゴリを選択してください" />
          ) : (
            <Form form={form} layout="vertical" disabled={!isEditMode}>
              <Form.Item
                label="カテゴリ名"
                name="categoryLabel"
                rules={[
                  { required: true, message: 'カテゴリ名を入力してください' },
                  { max: 255, message: 'カテゴリ名は255文字以内で入力してください' },
                ]}
              >
                <Input placeholder="例: カスタム設備" />
              </Form.Item>

              <Form.Item label="説明" name="description">
                <Input.TextArea placeholder="カテゴリの説明（任意）" rows={3} />
              </Form.Item>

              <Form.Item label="カラー" name="color">
                <HexColorPicker />
              </Form.Item>

              <Form.Item label="アイコン" name="icon">
                <IconPicker placeholder="アイコンを選択（任意）" />
              </Form.Item>

              <Form.Item label="ステータス" name="isActive" valuePropName="checked">
                <Checkbox disabled={!isEditMode}>有効</Checkbox>
              </Form.Item>

              {selectedCategory.usageCount !== undefined && (
                <Form.Item label="使用数">
                  <Badge count={selectedCategory.usageCount} showZero color="green" />
                  <span style={{ marginLeft: 8 }}>件の設備がこのカテゴリを使用しています</span>
                </Form.Item>
              )}

              <div style={{ padding: '12px', backgroundColor: token.colorFillQuaternary, borderRadius: '4px', marginTop: '16px' }}>
                <Space direction="vertical" size="small">
                  <div style={{ fontSize: '12px', color: token.colorTextSecondary }}>
                    ヒント: 表示順序の変更方法
                  </div>
                  <div style={{ fontSize: '12px', color: token.colorTextSecondary }}>
                    左のリストでカテゴリをドラッグ&ドロップして並び替えできます
                  </div>
                </Space>
              </div>
            </Form>
          )}
        </Card>
      </Col>

      {/* 新規作成モーダル */}
      <Modal
        title="新しい設備カテゴリを作成"
        open={isCreateModalVisible}
        onOk={handleCreate}
        onCancel={() => {
          setIsCreateModalVisible(false);
          form.resetFields();
        }}
        confirmLoading={createMutation.isPending}
        okText="作成"
        cancelText="キャンセル"
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="カテゴリ名"
            name="categoryLabel"
            rules={[
              { required: true, message: 'カテゴリ名を入力してください' },
              { max: 255, message: '255文字以内で入力してください' },
            ]}
          >
            <Input
              placeholder="例: カスタム設備"
            />
          </Form.Item>

          <Form.Item label="説明" name="description">
            <Input.TextArea placeholder="カテゴリの説明（任意）" rows={3} />
          </Form.Item>

          <Form.Item label="カラー" name="color">
            <HexColorPicker />
          </Form.Item>

          <Form.Item label="アイコン" name="icon" extra="カテゴリを視覚的に識別するためのアイコン">
            <IconPicker placeholder="アイコンを選択（任意）" />
          </Form.Item>
        </Form>
      </Modal>
    </Row>
  );
};

export default EquipmentCategoriesTab;
